import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { IconButton, MenuItem, Select, TextField, Typography } from '@material-ui/core'
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  SearchOutlined as SearachIcon
} from '@material-ui/icons'
import noProfileImage from 'assets/img/user-mock.png'
import clsx from 'clsx'
import lodash from 'lodash'
import moment from 'moment'
import { ROUTES } from 'routes'
import { CHAMPIONSHIP_TYPES, EVENT_RESULT_STATES, STRING_EMPTY } from 'utils/constants'
import { parseTimeSpan, timeFormat } from 'utils/functions'

import { SectionBlock } from '../SectionBlock'

import { useStyles } from './ResultsSection.style'

const ALL_OPTIONS = 'All'

const initialSelectedValues = {
  circuit: STRING_EMPTY,
  category: ALL_OPTIONS,
  gender: ALL_OPTIONS,
  searchText: STRING_EMPTY
}

const GENDERS = [
  { value: 'Female', label: 'Femenino' },
  { value: 'Male', label: 'Masculino' },
  { value: 'NonBinary', label: 'No binario' }
]

const getEffectiveLapsCount = (eventTime) => {
  const lapsCount = Number(eventTime?.lapsCount) || 0
  const isEmptyOrZeroLapTime = eventTime.totalTime === '00:00:00'

  const hasIncidentStatus =
    isEmptyOrZeroLapTime &&
    eventTime.status.toLowerCase() !== EVENT_RESULT_STATES.NORMAL.toLowerCase()

  return hasIncidentStatus ? Math.max(lapsCount - 1, 0) : lapsCount
}

const ResultsSection = () => {
  const classes = useStyles()

  const [searchInputText, setSearchInputText] = useState(STRING_EMPTY)
  const [selectedValues, setSelectedValues] = useState(initialSelectedValues)
  const [expandedRows, setExpandedRows] = useState({})

  const { championship, championshipResults } = useSelector((state) => state.championships)

  const {
    type,
    distanceAward,
    eventsCount,
    disputedEventsCount,
    events,
    computedEventsCount,
    lastEventAccreditationRequired,
    minLapPercentageRequired,
    fixtures,
    championshipPoints
  } = championship

  const isChampionshipForPoints = type === CHAMPIONSHIP_TYPES.POINTS.value

  const resetValues = useCallback(() => {
    if (!championshipResults) {
      setSelectedValues(initialSelectedValues)
      return
    }

    const championshipResultsSorted = lodash.sortBy(championshipResults, (x) =>
      distanceAward
        ? x.circuitCategories[0].circuitName.toLowerCase().trim()
        : x.circuitCategories[0].categoryName.toLowerCase().trim()
    )

    const props = distanceAward
      ? {
          circuitSelected: championshipResultsSorted[0].circuitCategories[0].circuitName,
          categorySelected: ALL_OPTIONS
        }
      : {
          categorySelected: championshipResultsSorted[0].circuitCategories[0].categoryName,
          circuitSelected: ALL_OPTIONS
        }

    const results = getResults(props)

    const genders = GENDERS.filter((x) => results.some((r) => r.competitor.gender === x.value))
    const genderSelected = genders.length > 1 ? ALL_OPTIONS : genders[0].value

    const categoriesBy = new Set(
      results.flatMap((r) => r.circuitCategories.map((c) => c.categoryName))
    )

    const categorySelected =
      categoriesBy.size > 1 ? ALL_OPTIONS : [...categoriesBy][0] || ALL_OPTIONS

    setSelectedValues({
      ...selectedValues,
      ...(distanceAward
        ? {
            circuit: props.circuitSelected,
            gender: genderSelected,
            category: categorySelected
          }
        : { category: props.categorySelected, gender: genderSelected, circuit: ALL_OPTIONS })
    })
  }, [championshipResults])

  useEffect(() => {
    resetValues()
  }, [resetValues])

  const renderProfileImage = (x) => {
    const profileImageUrl =
      Array.isArray(x.competitor.profileImages) && x.competitor.profileImages.length > 0
        ? x.competitor.profileImages[0]
        : noProfileImage

    return (
      <div
        className={classes.runnerImage}
        style={{
          backgroundImage: `url(${profileImageUrl})`
        }}
      />
    )
  }

  const getRunnerName = (x) =>
    x.competitor.runnerId ? (
      <Link
        variant='text'
        color='primary'
        className={classes.linkUser}
        to={`${ROUTES.USERS_ACCOUNTS.PROFILE}/${
          x.competitor.runnerUsername ?? x.competitor.runnerId
        }`}>
        {renderProfileImage(x)} &nbsp; {x.competitor.name}
      </Link>
    ) : (
      <Typography color='primary' className={classes.runnerName}>
        {x.competitor.name}
      </Typography>
    )

  const getCategories = () => {
    if (!championshipResults) return []

    const uniqueCategories = lodash
      .uniqBy(
        championshipResults
          .flatMap((x) => x.circuitCategories)
          .filter((c) => (distanceAward ? c.circuitName === selectedValues.circuit : true)),
        (x) => x.categoryName
      )
      .sort((x, y) => x.categoryName.trim().localeCompare(y.categoryName.trim()))

    const categoryTimes = lodash
      .chain(championshipResults.flatMap((x) => x.eventTimes))
      .groupBy((x) => x.categoryName)
      .mapValues((items) => lodash.groupBy(items, (x) => x.eventId))
      .value()

    return uniqueCategories.map((x) => ({
      name: x.categoryName,
      eventMaxLaps: Object.values(categoryTimes[x.categoryName]).map((d) => ({
        eventId: d[0].eventId,
        eventName: d[0].eventName,
        maxLaps: lodash.max(d.map((c) => getEffectiveLapsCount(c)))
      }))
    }))
  }

  const fixturesAssistancePoints = fixtures
    .flatMap((fixture) => {
      const point = fixture.points.find((p) => p.position === 0)

      return point
        ? [
            {
              dateNumber: fixture.dateNumber,
              points: point.points
            }
          ]
        : []
    })
    .sort((a, b) => a.dateNumber - b.dateNumber)
  const fixturePositionPoints = fixtures
    .filter((fixture) => fixture.points.some((point) => point.position !== 0))
    .map((fixture) => ({
      dateNumber: fixture.dateNumber,
      points: fixture.points.filter((point) => point.position !== 0)
    }))

  const getGenders = () => {
    if (!championshipResults) return []

    const { category, circuit } = selectedValues

    return [
      ...new Set(
        championshipResults
          .filter((x) =>
            !distanceAward
              ? x.circuitCategories.some((c) => c.categoryName === category)
              : x.circuitCategories.some(
                  (c) =>
                    c.circuitName === circuit &&
                    (category === ALL_OPTIONS || c.categoryName === category)
                )
          )
          .map((x) => x.competitor.gender)
      )
    ].map((x) => GENDERS.find((g) => g.value === x))
  }

  const getAssitencePoints = (dateNumber) =>
    !fixturesAssistancePoints.length
      ? championshipPoints.find((x) => x.position === 0)
      : fixturesAssistancePoints.find((f) => f.dateNumber === dateNumber)

  const getPositionPoints = (dateNumber, currentPosition) => {
    return !fixturePositionPoints.length
      ? championship.championshipPoints.find(
          (x) => x.position !== 0 && x.position === currentPosition
        )?.points ?? 0
      : fixturePositionPoints
          .find((f) => f.dateNumber === dateNumber)
          ?.points.find((p) => p.position === currentPosition)?.points ?? 0
  }

  const getPositionPointsFor = (time, lapRequirementMet, currentPositionPoints) => {
    if (
      time.status === EVENT_RESULT_STATES.DID_NOT_START ||
      time.status === EVENT_RESULT_STATES.DISQUALIFIED
    )
      return 0
    if (time.status === EVENT_RESULT_STATES.DROP_OUT || time.status === EVENT_RESULT_STATES.LAPPED)
      return lapRequirementMet ? currentPositionPoints * (time.eventPointFactor ?? 1) : 0

    return currentPositionPoints * (time.eventPointFactor ?? 1)
  }

  const getInscriptionProps = (inscription) => {
    const lastEvent = [...events].sort((a, b) => moment(a.from).diff(b.from)).at(-1)
    const lastEventDisputed =
      eventsCount !== disputedEventsCount ||
      !lastEvent.hasResultsSheetFile ||
      inscription.eventTimes.some(
        (x) => x.eventId === lastEvent.id && x.status !== EVENT_RESULT_STATES.DID_NOT_START
      )

    if (isChampionshipForPoints) {
      const championshipPoints = inscription.eventTimes.map((i) => {
        const eventIndex = events.findIndex((e) => e.id === i.eventId)
        const isDidNotStart = i.status === EVENT_RESULT_STATES.DID_NOT_START

        const assitencePoints = !isDidNotStart ? getAssitencePoints(eventIndex + 1) : { points: 0 }

        const currentPosition = !distanceAward ? i.position : i.overallPosition
        const currentPositionPoints = !isDidNotStart
          ? getPositionPoints(eventIndex + 1, currentPosition)
          : 0

        const currentMaxLapsList = distanceAward ? circuits : categories
        const circuitOrCategoryName = distanceAward ? i.circuitName : i.categoryName
        const lapRequirementMet = currentMaxLapsList.some(
          (c) =>
            c.name === circuitOrCategoryName &&
            c.eventMaxLaps.some((d) => {
              if (d.eventId !== i.eventId) return false

              const completedLapPercentage =
                d.maxLaps > 0 ? (getEffectiveLapsCount(i) / d.maxLaps) * 100 : 0

              return completedLapPercentage >= minLapPercentageRequired
            })
        )

        const positionPoints = getPositionPointsFor(i, lapRequirementMet, currentPositionPoints)

        const currentPoints = (assitencePoints?.points ?? 0) + positionPoints

        return {
          ...i,
          points: currentPoints
        }
      })

      const bestPoints = championshipPoints
        .sort((a, b) => b.points - a.points)
        .slice(0, computedEventsCount)

      return {
        lastEventDisputed,
        totalPoints: bestPoints.reduce((acc, x) => acc + x.points, 0),
        points: championshipPoints.map((x) => ({
          ...x,
          usePoints: bestPoints.some((p) => p.position === x.position && x.eventId === p.eventId)
        }))
      }
    } else {
      const bestTimes = inscription.eventTimes
        .filter((x) => x.status !== EVENT_RESULT_STATES.DID_NOT_START)
        .sort((x, y) => parseTimeSpan(x.totalTime) - parseTimeSpan(y.totalTime))
        .slice(0, computedEventsCount)

      return {
        lastEventDisputed,
        eventTimes: inscription.eventTimes.map((x) => ({
          ...x,
          useTime: bestTimes.some(
            (p) =>
              parseTimeSpan(p.totalTime) === parseTimeSpan(x.totalTime) && x.eventId === p.eventId
          )
        }))
      }
    }
  }

  const getCircuits = () => {
    if (!championshipResults) return []

    const uniqueCircuits = lodash
      .uniqBy(
        championshipResults.flatMap((r) => r.circuitCategories),
        (x) => x.circuitName
      )
      .sort((x, y) =>
        x.circuitName.trim().toLowerCase().localeCompare(y.circuitName.trim().toLowerCase())
      )

    const circuitTimes = lodash
      .chain(championshipResults.flatMap((x) => x.eventTimes))
      .groupBy((x) => x.circuitName)
      .mapValues((items) => lodash.groupBy(items, (x) => x.eventId))
      .value()

    return uniqueCircuits.map((x) => ({
      name: x.circuitName,
      eventMaxLaps: Object.values(circuitTimes[x.circuitName]).map((d) => ({
        eventId: d[0].eventId,
        eventName: d[0].eventName,
        maxLaps: lodash.max(d.map((c) => getEffectiveLapsCount(c)))
      }))
    }))
  }

  const getResults = ({ categorySelected, circuitSelected } = {}) => {
    const { circuit, category } = selectedValues

    return championshipResults.filter((x) =>
      x.circuitCategories.some(
        (c) =>
          (categorySelected === ALL_OPTIONS ||
            category === ALL_OPTIONS ||
            c.categoryName === (categorySelected || category)) &&
          (circuitSelected === ALL_OPTIONS ||
            circuit === ALL_OPTIONS ||
            c.circuitName === (circuitSelected || circuit))
      )
    )
  }

  const sortByLastPosition = (x, y) => {
    if (x.totalPoints === 0 && y.totalPoints === 0) {
      const lastPositionX = x.eventTimes
        .sort((a, b) => moment(a.eventFrom).diff(b.eventFrom))
        .at(-1)
      const lastPositionY = y.eventTimes
        .sort((a, b) => moment(a.eventFrom).diff(b.eventFrom))
        .at(-1)

      return !distanceAward
        ? lastPositionX.position - lastPositionY.position
        : lastPositionX.overallPosition - lastPositionY.overallPosition
    }

    const pointsX = x.points
      .filter((p) => p.usePoints)
      .sort((a, b) => moment(a.eventFrom).diff(b.eventFrom))

    const pointsY = y.points
      .filter((p) => p.usePoints)
      .sort((a, b) => moment(a.eventFrom).diff(b.eventFrom))

    const lastEventsX = pointsX.at(-1)
    const lastEventsY = pointsY.at(-1)

    return !distanceAward
      ? lastEventsX.position - lastEventsY.position
      : lastEventsX.overallPosition - lastEventsY.overallPosition
  }

  const getPositions = () => {
    if (!championshipResults) return []

    return getResults()
      .map((x) => ({
        ...x,
        ...getInscriptionProps(x)
      }))
      .sort((x, y) => {
        if (isChampionshipForPoints)
          return y.totalPoints - x.totalPoints || sortByLastPosition(x, y)

        const eventRestCount = eventsCount - disputedEventsCount

        const xHasEnoughTimes = x.eventTimes.length + eventRestCount >= computedEventsCount
        const yHasEnoughTimes = y.eventTimes.length + eventRestCount >= computedEventsCount

        if (xHasEnoughTimes && !yHasEnoughTimes) return -1
        if (!xHasEnoughTimes && yHasEnoughTimes) return 1

        return (
          parseTimeSpan(x.totalTime) - parseTimeSpan(y.totalTime) ||
          y.eventTimes.length - x.eventTimes.length
        )
      })
      .sort((x, y) => {
        const xZero = parseTimeSpan(x.totalTime) === 0
        const yZero = parseTimeSpan(y.totalTime) === 0

        if (xZero !== yZero) return xZero ? 1 : -1

        return 0
      })
      .sort((x, y) => {
        const disabledSorted =
          !lastEventAccreditationRequired ||
          disputedEventsCount !== eventsCount ||
          x.lastEventDisputed === y.lastEventDisputed

        if (disabledSorted) return 0

        return x.lastEventDisputed ? -1 : 1
      })
      .map((x, index) => ({
        ...x,
        order: index + 1
      }))
  }

  const showPoint = (points, time) => {
    if (time.status === EVENT_RESULT_STATES.DID_NOT_START) return 0

    const point = points.find((p) => p.eventId === time.eventId) ?? {}

    return point.usePoints ? point.points ?? '-' : `(${point.points})` ?? '-'
  }

  const showPosition = (eventTime) => {
    return !distanceAward ? eventTime.position : eventTime.overallPosition
  }

  const showTime = (eventTime) => {
    if (eventTime.status === EVENT_RESULT_STATES.DID_NOT_START) return 'DNS'

    if (isChampionshipForPoints) return eventTime.totalTime

    return eventTime.useTime
      ? timeFormat(eventTime.totalTime)
      : `(${timeFormat(eventTime.totalTime)})`
  }

  const getTotalTimeOrPoints = (result) => {
    if (result.eventTimes.every((e) => e.status === EVENT_RESULT_STATES.DID_NOT_START))
      return isChampionshipForPoints ? 0 : 'DNS'

    if (lastEventAccreditationRequired && !result.lastEventDisputed) return '-'

    return isChampionshipForPoints ? result.totalPoints : timeFormat(result.totalTime)
  }

  const categories = getCategories()
  const circuits = getCircuits()
  const genders = getGenders()

  const positions = getPositions().filter(
    (x) =>
      x.competitor.name
        .toLowerCase()
        .trim()
        .includes(selectedValues.searchText.toLowerCase().trim()) &&
      (selectedValues.gender === ALL_OPTIONS || x.competitor.gender === selectedValues.gender)
  )

  return (
    <SectionBlock title='Clasificación' className={classes.section}>
      <form
        className={classes.form}
        onSubmit={(e) => {
          e.preventDefault()
          setSelectedValues({ ...selectedValues, searchText: searchInputText })
        }}>
        <TextField
          variant='outlined'
          color='primary'
          placeholder='Buscar por nombre del competidor'
          className={classes.search}
          size='small'
          value={searchInputText}
          onChange={(e) => setSearchInputText(e.target.value)}
          InputProps={{
            classes: {
              root: classes.search,
              notchedOutline: classes.notchedOutline
            },
            endAdornment: (
              <IconButton className={classes.button} color='primary' size='small' type='submit'>
                <SearachIcon />
              </IconButton>
            )
          }}
        />
      </form>
      <Typography color='primary' className={classes.helperText}>
        Seleccione la categoría para conocer los resultados de la misma
      </Typography>

      <div className={classes.selectContainer}>
        {distanceAward && (
          <Select
            className={classes.select}
            fullWidth
            variant='outlined'
            color='primary'
            margin='dense'
            onChange={(e) => {
              const circuitSelected = e.target.value

              const results = getResults({ circuitSelected, categorySelected: ALL_OPTIONS })

              const genders = GENDERS.filter((x) =>
                results.some((r) => r.competitor.gender === x.value)
              )
              const genderSelected = genders.length > 1 ? ALL_OPTIONS : genders[0].value

              const categoriesBy = new Set(
                results.flatMap((r) => r.circuitCategories.map((c) => c.categoryName))
              )

              const categorySelected =
                categoriesBy.size > 1 ? ALL_OPTIONS : [...categoriesBy][0] || ALL_OPTIONS

              setSelectedValues({
                ...selectedValues,
                circuit: e.target.value,
                category: categorySelected,
                gender: genderSelected
              })
            }}
            value={selectedValues.circuit}
            renderValue={(value) => `Distancia (${value === ALL_OPTIONS ? 'Todos' : value})`}
            IconComponent={(props) => <KeyboardArrowDownIcon {...props} color='secondary' />}>
            {circuits.map((x, index) => (
              <MenuItem key={index} value={x.name}>
                {x.name}
              </MenuItem>
            ))}
          </Select>
        )}

        <Select
          className={classes.select}
          fullWidth
          variant='outlined'
          color='primary'
          margin='dense'
          onChange={(e) => {
            const categorySelected = e.target.value
            const results = getResults({ categorySelected })

            const genders = GENDERS.filter((x) =>
              results.some((r) => r.competitor.gender === x.value)
            )
            const genderSelected = genders.length > 1 ? ALL_OPTIONS : genders[0].value

            setSelectedValues({
              ...selectedValues,
              category: categorySelected,
              gender: genderSelected
            })
          }}
          value={selectedValues.category}
          renderValue={(value) => `Categoría (${value === ALL_OPTIONS ? 'Todos' : value})`}
          IconComponent={(props) => <KeyboardArrowDownIcon {...props} color='secondary' />}>
          {distanceAward && categories.length > 1 && <MenuItem value={ALL_OPTIONS}>Todos</MenuItem>}
          {categories.map((x, index) => (
            <MenuItem key={index} value={x.name}>
              {x.name}
            </MenuItem>
          ))}
        </Select>

        <Select
          className={classes.select}
          fullWidth
          variant='outlined'
          color='primary'
          margin='dense'
          value={selectedValues.gender}
          onChange={(e) => setSelectedValues({ ...selectedValues, gender: e.target.value })}
          renderValue={(value) => {
            const gender = genders.find((g) => g.value === value) ?? { label: 'Todos' }
            return `Género (${gender.label})`
          }}
          IconComponent={(props) => <KeyboardArrowDownIcon {...props} color='secondary' />}>
          {genders.length > 1 && <MenuItem value={ALL_OPTIONS}>Todos</MenuItem>}

          {genders.map((x, index) => (
            <MenuItem key={index} value={x.value}>
              {x.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      <table className={classes.table}>
        <thead>
          <tr>
            <th>PUESTO</th>
            <th>COMPETIDOR/A</th>
            <th className={classes.cityNameCol}>CATEGORÍA</th>
            <th className={classes.largueCol}>PROCEDENCIA</th>
            <th className={classes.largueCol}>EVENTOS</th>
            {isChampionshipForPoints ? <th>PUNTOS</th> : <th>TIEMPO</th>}
            <th className={classes.expandButtonCol}></th>
          </tr>
        </thead>

        <tbody>
          {!!positions?.length &&
            positions.map((x) => {
              const categories = [...new Set(x.circuitCategories.map((c) => c.categoryName))].join(
                ', '
              )
              const activeTimes = !distanceAward
                ? x.eventTimes
                : x.eventTimes.filter(
                    (x) =>
                      selectedValues.category === ALL_OPTIONS ||
                      x.categoryName === selectedValues.category
                  )

              return (
                <Fragment key={x.order}>
                  <tr
                    className={clsx(
                      classes.row,
                      expandedRows[x.competitor.idNumber] && 'expanded'
                    )}>
                    <td className={classes.col}>
                      {!lastEventAccreditationRequired || x.lastEventDisputed ? x.order : '-'}
                    </td>
                    <td>{getRunnerName(x)}</td>
                    <td title={categories}>{categories}</td>
                    <td className={classes.largueCol}>{x.competitor.cityName}</td>
                    <td className={clsx(classes.col, classes.largueCol)}>
                      {activeTimes.length}/{eventsCount}
                    </td>
                    <td className={classes.col}>{getTotalTimeOrPoints(x)}</td>
                    <td className={classes.expandButtonCol}>
                      <IconButton
                        className={classes.expandButton}
                        onClick={() =>
                          setExpandedRows({
                            ...expandedRows,
                            [x.competitor.idNumber]: !expandedRows[x.competitor.idNumber]
                          })
                        }>
                        {expandedRows[x.competitor.idNumber] ? (
                          <KeyboardArrowUpIcon color='secondary' />
                        ) : (
                          <KeyboardArrowDownIcon color='primary' />
                        )}
                      </IconButton>
                    </td>
                  </tr>

                  {expandedRows[x.competitor.idNumber] && (
                    <tr>
                      <td colSpan={100} className={classes.innerTableCell}>
                        <table>
                          <thead>
                            <tr>
                              <th>NOMBRE DE EVENTO</th>
                              <th>PUESTO</th>
                              <th>TIEMPO</th>
                              {isChampionshipForPoints && <th>PUNTOS</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {activeTimes.map((t) => (
                              <tr key={t.eventId}>
                                <td>
                                  <Link
                                    className={classes.eventLink}
                                    to={`${ROUTES.EVENTS.PROFILE}/${t.eventSlugUrl}/${t.circuitCategoryId}`}>
                                    {t.eventName}
                                  </Link>
                                </td>
                                <td className={classes.col}>{showPosition(t)}</td>
                                <td>{showTime(t)}</td>
                                {isChampionshipForPoints && (
                                  <td className={classes.col}>{showPoint(x.points, t)}</td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
        </tbody>
      </table>
    </SectionBlock>
  )
}

export default ResultsSection
